import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select, { components } from "react-select";
import "./LoanLandingPage.css";
import { Helmet } from "react-helmet-async";
// Keep your existing base64 logo string here — unchanged, not reproducing it to save space
const LOGO_SRC = "data:image/jpeg;base64,/9j/....";

/* ─────────────────────────────────────────────
   COUNTRY SELECT RENDERERS (with flag icons)
───────────────────────────────────────────── */
const CountryOption = (props) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="lnp-select-flex">
        <img src={`/countrylogos/${data.value}.png`} alt="" className="lnp-flag-icon" />
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
};

const CountrySingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="lnp-select-flex">
        <img src={`/countrylogos/${data.value}.png`} alt="" className="lnp-flag-icon" />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

const LOAN_RANGES = [
  "Select range",
  "Up to ₹10 Lakhs",
  "₹10–25 Lakhs",
  "₹25–50 Lakhs",
  "₹50 Lakhs – ₹1 Crore",
  "₹1–2 Crore",
  "₹2 Crore+",
];

const COLLATERAL_OPTIONS = ["Select", "Yes", "No"];

const FEATURES = [
  { num: "0%", label: "Service fee from FlyUrDream" },
  { num: "48hrs", label: "Pre-approval turnaround" },
  { num: "₹40L", label: "No collateral required up to" },
  { num: "15+", label: "Bank & NBFC partners compared" },
];

const BANKS = [
  { name: "State Bank of India", meta: "Up to ₹2 Cr · From 8.5% p.a." },
  { name: "HDFC Credila", meta: "Up to ₹1.2 Cr · From 9.2% p.a." },
  { name: "Avanse Financial Services", meta: "Up to ₹80 L · From 10.1% p.a." },
  { name: "Auxilo Finserve", meta: "Up to ₹75 L · From 10.5% p.a." },
];

const STEPS = [
  {
    title: "Check your eligibility",
    desc: "Share your course, university, and family income details — takes 60 seconds.",
  },
  {
    title: "We compare lenders",
    desc: "Our loan specialist matches you with the best-fit bank or NBFC offer.",
  },
  {
    title: "Get pre-approved",
    desc: "Receive a pre-approval letter within 48 hours — even before your university offer.",
  },
  {
    title: "Disbursement & visa filing",
    desc: "Loan sanction letter is used to strengthen your visa application.",
  },
];

const FAQS = [
  {
    q: "Do I need collateral?",
    a: "For loans up to ₹40 Lakhs, many of our partner banks offer collateral-free loans based on co-applicant income and academic profile.",
  },
  {
    q: "Who can be a co-applicant?",
    a: "Typically a parent, guardian, or close relative with a stable income. We'll guide you on the exact requirements for your chosen lender.",
  },
  {
    q: "Does FlyUrDream charge for this service?",
    a: "No — our loan guidance is completely free. We're compensated by partner banks, not by you.",
  },
];

const INITIAL_FORM = {
  fullName: "",
  email: "",
  countryCode: "+91",
  phone: "",
  destination: "",
  loanAmount: LOAN_RANGES[0],
  collateral: COLLATERAL_OPTIONS[0],
  consentMarketing: false,
  consentWhatsapp: false,
  consentFinancial: false,
};

export default function LoanLandingPage() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [enquiryId, setEnquiryId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const formCardRef = useRef(null);

  // Countries (for Destination flag-select)
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/GetCountries`);
        const trimmedCountries = response.data.map((c) => ({
          ...c,
          countryCode: c.countryCode?.trim().toLowerCase(),
        }));
        setCountries(trimmedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, []);

  const countryOptions = countries.map((c) => ({
    value: c.countryCode,
    label: c.countryName,
  }));

  const handleCountryChange = (selected) => {
    setForm((prev) => ({ ...prev, destination: selected ? selected.label : "" }));
  };

  // Load the Poppins/Inter fonts used by this page
  useEffect(() => {
    const id = "lnp-google-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  // ── IP address capture, same pattern as RecruitmentPartner ──
  const [ipAddress, setIpAddress] = useState("");
  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setIpAddress(response.data.ip);
      } catch (error) {
        console.error("Error fetching IP address:", error);
        setIpAddress("Unable to fetch IP");
      }
    };
    fetchIpAddress();
  }, []);

  // ── handleChange with digit-only phone + alpha-only name enforcement ──
  const handleChange = (field) => (e) => {
    let value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    if (field === "phone") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (field === "fullName") {
      value = value.replace(/[^A-Za-z\s]/g, "");
    }

    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // ── Validation: everything mandatory, including all 3 checkboxes ──
  const validateForm = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    const nameRegex = /^[A-Za-z\s]+$/;

    if (!form.fullName.trim()) {
      toast.error("Please enter your name");
      return false;
    }
    if (!nameRegex.test(form.fullName.trim())) {
      toast.error("Name should contain only letters");
      return false;
    }
    if (!form.email.trim()) {
      toast.error("Please enter your email");
      return false;
    }
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    if (!form.phone.trim()) {
      toast.error("Please enter your phone number");
      return false;
    }
    if (!phoneRegex.test(form.phone)) {
      toast.error("Phone number must be exactly 10 digits");
      return false;
    }
    if (!form.destination) {
      toast.error("Please select where you want to study");
      return false;
    }
    if (form.loanAmount === LOAN_RANGES[0]) {
      toast.error("Please select the loan amount required");
      return false;
    }
    if (form.collateral === COLLATERAL_OPTIONS[0]) {
      toast.error("Please select collateral availability");
      return false;
    }
    if (!form.consentMarketing) {
      toast.error("Please accept the marketing consent to proceed");
      return false;
    }
    // if (!form.consentWhatsapp) {
    //   toast.error("Please accept the WhatsApp updates consent to proceed");
    //   return false;
    // }
    if (!form.consentFinancial) {
      toast.error("Please accept the financial data sharing consent to proceed");
      return false;
    }
    return true;
  };

  // ── Submit: PATCH if enquiryId exists (update), else POST (create) ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

const payload = {
  Enquiry_Id: enquiryId ? Number(enquiryId) || 0 : null,
  CompanyId: {  CompanyID: window.location.origin === "https://flyurdream.com" ? 160 : 2,
          UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
   },
         UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
  FirstName: form.fullName,
  LastName: "",
  Mobile_Number: form.phone,
  Country_Code: form.countryCode,
  Email: form.email,
  Preferred_Contact_Time: null,
  Lead_Type: { FieldType: "Loan" },
  Lead_Stage: { FieldType: "New" },
  Source:{

fieldType: "Manual"
  },
  Source_Company: "FlyUrDream",
  CollegeVisit_CollegeName: null,
  CollegeVisit_City: null,
  CollegeVisit_Date: null,
  Batch: "",
  Captured_By: null,
  Captured_By_System: "LoanLandingPage",
  Assigned_To: null,
  Converted_By: null,
  Converted_Application_Id: null,
  Is_Consented: Boolean(form.consentMarketing),
  Consent_Via: form.consentWhatsapp ? { FieldType: "WhatsApp" } : null,
  Consent_Financial_Share: Boolean(form.consentFinancial),
  Consent_WhatsAppUpdates: Boolean(form.consentWhatsapp),
  Preferred_Country: null,
  Preferred_CountryName: form.destination || "",
  Course_Interest: "",
  Study_Level: "",
  University: null,
  University_Interest: "",
  Intake: null,
  Loan_Amount_Range:
    form.loanAmount !== LOAN_RANGES[0]
      ? { FieldType: form.loanAmount }
      : null,
  Has_Collateral:
    form.collateral === "Yes"
      ? true
      : form.collateral === "No"
      ? false
      : null,
  Notes: "",
  CreatedBy: null,
  IsArchived: false,
};

console.log("before", payload);

const isUpdate = Boolean(enquiryId);
const url =  `${process.env.REACT_APP_API_BASE_URL}/CreateEnquiry`;

    setSubmitting(true);
    try {
      const response =  await axios.post(url, payload);

      toast.success(response.data.message || "Thanks! Our loan specialist will reach out shortly.");

      if (response.data?.enquiry_Id) {
        setEnquiryId(response.data.enquiry_Id);
      }

      console.log("after", response.data);

      // ── Reset form after successful upsert ──
      setForm(INITIAL_FORM);
    } catch (error) {
      toast.error("Error submitting enquiry. Please try again.");
      console.error("Enquiry submit failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToForm = () => {
    formCardRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="lnp-root">
      {/* <div className="lnp-header">
        <img src={LOGO_SRC} alt="" />
       
      </div> */}
 <Helmet>
        <title>Education Loans for Study Abroad — No Collateral Up to ₹40L | FlyUrDream</title>
        <meta name="description" content="Get pre-approved for an education loan up to ₹2 Crore within 48 hours. 15+ bank & NBFC partners compared, no service fee." />
        <link rel="canonical" href="https://flyurdream.com/LoanLandingPage" />

        <meta property="og:title" content="Education Loans for Study Abroad | FlyUrDream" />
        <meta property="og:description" content="No collateral needed up to ₹40 Lakhs. Compare 15+ bank & NBFC partners, get pre-approved in 48 hours." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://flyurdream.com/LoanLandingPage" />

        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="lnp-hero">
       
        <div className="lnp-hero-inner">
          <div className="lnp-hero-badge">🏦 15+ Bank &amp; NBFC Partners</div>
          <h1>Fund Your Study Abroad Dream. No Barrier — No Collateral Needed.</h1>
          <p className="lnp-hero-sub">
            FlyUrDream helps Indian students secure education loans up to ₹2
            Crore through trusted bank and NBFC partners — with pre-approval
            before your university offer.
          </p>
           <a href="#lnp-form" className="lnp-header-cta">
         <div className="lnp-header-cta-btn">
           Check Eligibility →
         </div>
        </a>
          <div className="lnp-hero-stats">
            <div className="lnp-hstat">
              <div className="lnp-hstat-num">₹2Cr</div>
              <div className="lnp-hstat-lbl">Max Loan Amount</div>
            </div>
            <div className="lnp-hstat">
              <div className="lnp-hstat-num">₹40L</div>
              <div className="lnp-hstat-lbl">No Collateral Limit</div>
            </div>
            <div className="lnp-hstat">
              <div className="lnp-hstat-num">15+</div>
              <div className="lnp-hstat-lbl">Bank Partners</div>
            </div>
            <div className="lnp-hstat">
              <div className="lnp-hstat-num">48hr</div>
              <div className="lnp-hstat-lbl">Pre-Approval</div>
            </div>
          
          </div>
        </div>
      </div>

      <div className="lnp-layout" id="lnp-form">
        <div className="lnp-content-col">
          <div className="lnp-sec-block">
            <div className="lnp-sec-label">Why Students Choose Us</div>
            <h2>Money should never stop your dream.</h2>
            <p>
              We work with India's leading banks and NBFCs to match you with
              the loan that fits your university, course, and family's
              financial situation — at no extra cost to you.
            </p>
            <div className="lnp-feature-grid">
              {FEATURES.map((f) => (
                <div className="lnp-feature-card" key={f.label}>
                  <div className="lnp-feature-num">{f.num}</div>
                  <div className="lnp-feature-lbl">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lnp-sec-block">
            <div className="lnp-sec-label">Our Partner Banks</div>
            <h2>Compare offers from 15+ lenders.</h2>
            <div className="lnp-bank-list">
              {BANKS.map((b) => (
                <div className="lnp-bank-row" key={b.name}>
                  <div className="lnp-bank-ico">🏦</div>
                  <div className="lnp-bank-info">
                    <div className="lnp-bank-name">{b.name}</div>
                    <div className="lnp-bank-meta">{b.meta}</div>
                  </div>
                  <span className="lnp-bank-avail">Available</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lnp-sec-block">
            <div className="lnp-sec-label">How It Works</div>
            <h2>Loan approval in 4 simple steps.</h2>
            <div className="lnp-step-list">
              {STEPS.map((s, i) => (
                <div className="lnp-step-item" key={s.title}>
                  <div className="lnp-step-num">{i + 1}</div>
                  <div>
                    <div className="lnp-step-title">{s.title}</div>
                    <div className="lnp-step-desc">{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lnp-sec-block">
            <div className="lnp-sec-label">FAQs</div>
            <h2>Common questions about education loans.</h2>
            {FAQS.map((f) => (
              <div className="lnp-faq-item" key={f.q}>
                <div className="lnp-faq-q">{f.q}</div>
                <div className="lnp-faq-a">{f.a}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lnp-form-card" ref={formCardRef}>
          <h3>Check Your Loan Eligibility</h3>
          <div className="lnp-fsub">Free · No cost · 60 seconds</div>
          <form onSubmit={handleSubmit}>
            <div className="lnp-f-row">
              <label>FULL NAME *</label>
              <input
                type="text"
                placeholder="Enter your full name"
                value={form.fullName}
                onChange={handleChange("fullName")}
              />
            </div>
            <div className="lnp-f-row">
              <label>EMAIL *</label>
              <input
                type="email"
                placeholder="you@email.com"
                value={form.email}
                onChange={handleChange("email")}
              />
            </div>
            <div className="lnp-f-row">
              <label>PHONE / WHATSAPP *</label>
              <div className="lnp-phone-grid">
                <select
                  value={form.countryCode}
                  onChange={handleChange("countryCode")}
                >
                  <option>+91</option>
                </select>
                <input
                  type="text"
                  placeholder="98765 43210"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  maxLength={10}
                />
              </div>
            </div>
            <div className="lnp-f-row">
              <label>WHERE DO YOU WANT TO STUDY? *</label>
              <Select
                classNamePrefix="lnp-country-select"
                options={countryOptions}
                components={{ Option: CountryOption, SingleValue: CountrySingleValue }}
                value={countryOptions.find((opt) => opt.label === form.destination) || null}
                onChange={handleCountryChange}
                placeholder="Select destination"
                isSearchable
              />
            </div>
            <div className="lnp-f-row">
              <label>LOAN AMOUNT REQUIRED *</label>
              <select
                value={form.loanAmount}
                onChange={handleChange("loanAmount")}
              >
                {LOAN_RANGES.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="lnp-f-row">
              <label>COLLATERAL AVAILABLE? *</label>
              <select
                value={form.collateral}
                onChange={handleChange("collateral")}
              >
                {COLLATERAL_OPTIONS.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="lnp-consent-block">
              <div className="lnp-consent-row">
                <input
                  type="checkbox"
                  id="lnp-consent-marketing-loan"
                  checked={form.consentMarketing}
                  onChange={handleChange("consentMarketing")}
                />
                <label htmlFor="lnp-consent-marketing-loan">
                  I consent to receiving information (by email, phone, or
                  SMS/WhatsApp) about education loan options from
                  FlyUrDream, in line with the <a href="/privacy-policy">Privacy Policy</a>. *
                </label>
              </div>
              <div className="lnp-consent-row">
                <input
                  type="checkbox"
                  id="lnp-consent-whatsapp-loan"
                  checked={form.consentWhatsapp}
                  onChange={handleChange("consentWhatsapp")}
                />
                <label htmlFor="lnp-consent-whatsapp-loan">
                  I would like to receive WhatsApp updates on my loan
                  application status and offer comparisons. 
                </label>
              </div>
              <div className="lnp-consent-row lnp-financial">
                <input
                  type="checkbox"
                  id="lnp-consent-financial"
                  checked={form.consentFinancial}
                  onChange={handleChange("consentFinancial")}
                />
                <label htmlFor="lnp-consent-financial">
                  <b>
                    I consent to FlyUrDream sharing my financial and personal
                    details with partner banks/NBFCs
                  </b>{" "}
                  solely for the purpose of processing my education loan
                  eligibility and application. *
                </label>
              </div>
            </div>

            <button type="submit" className="lnp-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Check Eligibility Free →"}
            </button>
          </form>
          <div className="lnp-trust-line">🔒 100% free · No hidden charges</div>
        </div>
      </div>

      {/* <div className="lnp-sticky-mobile-cta">
        <button onClick={scrollToForm}>Check Loan Eligibility →</button>
      </div> */}

      <div className="lnp-footer">
        FlyUrDream · Education loan guidance for Indian students
        <br />
        <a href="/privacy-policy">Privacy Policy</a> · <a href="/terms">Terms</a> ·
        study@flyurdream.com
      </div>

      <ToastContainer />
    </div>
  );
}